import { memo, useCallback, useMemo } from 'react';

import { useTrackClickedEllipsisMenuButton } from '@air/analytics';
import { type PublicClip } from '@air/api';
import { type Clip } from '@air/api/types';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { itemIsSelectedSelector } from '@air/redux-selected-items';

import { CardEllipsisMenu } from '~/components/Gallery/CardEllipsisMenu';
import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { useWorkspaceAgentMenuDropdownClassName } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentMenuDropdownClassName';
import { type GetItemMenuOptions } from '~/constants/MenuOptions';
import { useAirSelector } from '~/utils/ReduxUtils';

export type GalleryFileMenuProps = {
  clip: Clip | PublicClip;
  getItemMenuOptions: GetItemMenuOptions;
  getSelectionMenuOptions: GetSelectionMenuOptionsType;
  setIsMenuOpen: (value: boolean) => void;
  trigger: JSX.Element;
};

export const GalleryFileMenu = memo(
  ({ clip, getItemMenuOptions, getSelectionMenuOptions, setIsMenuOpen, trigger }: GalleryFileMenuProps) => {
    const { trackClickedEllipsisMenuButton } = useTrackClickedEllipsisMenuButton();
    const { selectionMenuDropdownClassName } = useWorkspaceAgentMenuDropdownClassName();
    const isSelected = useAirSelector((st) => itemIsSelectedSelector(st, clip.id));

    const selectionMenuOptions = getSelectionMenuOptions('gallery-multi-select-ellipsis-menu');
    const ellipsisMenuOptions = getItemMenuOptions({ trackLocation: 'gallery-item-ellipsis-menu' });

    const onMenuChange = useCallback(
      (isOpen: boolean) => {
        if (isOpen) {
          trackClickedEllipsisMenuButton({ location: 'asset-item' });
        }

        setIsMenuOpen(isOpen);
      },
      [setIsMenuOpen, trackClickedEllipsisMenuButton],
    );

    const isMultiSelectMenu = isSelected && selectionMenuOptions.length > 0;

    const menuOptions = useMemo(() => {
      const menuOptionsArray: DropdownMenuOption[] = [];

      if (isMultiSelectMenu) {
        menuOptionsArray.push(...selectionMenuOptions);
      } else {
        menuOptionsArray.push(...ellipsisMenuOptions);
      }

      return menuOptionsArray;
    }, [ellipsisMenuOptions, isMultiSelectMenu, selectionMenuOptions]);

    if (!menuOptions?.length) {
      return null;
    }

    return (
      <CardEllipsisMenu
        title="More file options"
        menuProps={{
          dropdownClassName: isMultiSelectMenu ? selectionMenuDropdownClassName : undefined,
          options: menuOptions,
        }}
        onMenuChange={onMenuChange}
        trigger={trigger}
      />
    );
  },
);

GalleryFileMenu.displayName = 'GalleryFileMenu';
